<template>
  <div class="homework-details">
    <!-- Summary Cards -->
    <div class="row mb-4">
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card stats-card">
          <div class="card-body text-center">
            <feather-icon icon="FileTextIcon" size="24" class="text-primary mb-2" />
            <h4 class="mb-1">{{ homeworkData.length || 0 }}</h4>
            <small class="text-muted">Всего заданий</small>
          </div>
        </div>
      </div>
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card stats-card">
          <div class="card-body text-center">
            <feather-icon icon="CheckSquareIcon" size="24" class="text-success mb-2" />
            <h4 class="mb-1">{{ getGradedCount() }}</h4>
            <small class="text-muted">Оценено</small>
          </div>
        </div>
      </div>
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card stats-card">
          <div class="card-body text-center">
            <feather-icon icon="ClockIcon" size="24" class="text-warning mb-2" />
            <h4 class="mb-1">{{ getPendingCount() }}</h4>
            <small class="text-muted">Ожидают оценки</small>
          </div>
        </div>
      </div>
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card stats-card">
          <div class="card-body text-center">
            <feather-icon icon="TrendingUpIcon" size="24" class="text-info mb-2" />
            <h4 class="mb-1">{{ getAverageScore() }}</h4>
            <small class="text-muted">Средний балл</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Grade Distribution -->
    <div class="row mb-4">
      <div class="col-lg-6 mb-4">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">Распределение оценок</h5>
          </div>
          <div class="card-body">
            <canvas ref="gradeChart" width="300" height="200"></canvas>
            <div class="grade-legend mt-3">
              <div class="row">
                <div v-for="(count, grade) in gradeDistribution" :key="grade" class="col-6 col-md-4 mb-2">
                  <div class="d-flex align-items-center">
                    <div
                      class="legend-color mr-2"
                      :style="{ backgroundColor: getGradeColor(grade) }"
                    ></div>
                    <small>{{ grade }}: {{ count }}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-6 mb-4">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">Динамика выполнения</h5>
          </div>
          <div class="card-body">
            <canvas ref="performanceChart" width="300" height="200"></canvas>
          </div>
        </div>
      </div>
    </div>

    <!-- Homework List -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">Список домашних заданий</h5>
            <div class="d-flex align-items-center">
              <!-- Status Filter -->
              <b-form-select
                v-model="statusFilter"
                :options="statusOptions"
                size="sm"
                class="mr-2"
                style="width: auto;"
              />
              <!-- Grade Filter -->
              <b-form-select
                v-model="gradeFilter"
                :options="gradeOptions"
                size="sm"
                class="mr-2"
                style="width: auto;"
              />
              <!-- Search -->
              <b-input-group size="sm" style="width: 200px;">
                <b-form-input
                  v-model="searchQuery"
                  placeholder="Поиск заданий..."
                />
                <b-input-group-append>
                  <b-button variant="outline-secondary" size="sm">
                    <feather-icon icon="SearchIcon" size="14" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </div>
          </div>
          <div class="card-body p-0">
            <!-- Homework Table -->
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="thead-light">
                  <tr>
                    <th>Задание</th>
                    <th>Дата задания</th>
                    <th>Срок сдачи</th>
                    <th>Дата сдачи</th>
                    <th>Статус</th>
                    <th>Оценка</th>
                    <th>Балл</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="homework in paginatedHomework"
                    :key="homework.id"
                    class="homework-row"
                  >
                    <td>
                      <div>
                        <strong>{{ homework.homework_title }}</strong>
                        <br>
                        <small class="text-muted">{{ getHomeworkTypeText(homework.homework_type) }}</small>
                      </div>
                    </td>
                    <td>
                      <span>{{ formatDate(homework.assigned_at) }}</span>
                    </td>
                    <td>
                      <span v-if="homework.due_at" :class="getDueDateClass(homework.due_at, homework.status)">
                        {{ formatDate(homework.due_at) }}
                      </span>
                      <span v-else class="text-muted">Без срока</span>
                    </td>
                    <td>
                      <span v-if="homework.submitted_at">
                        {{ formatDate(homework.submitted_at) }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <b-badge :variant="getStatusVariant(homework.status)">
                        {{ getStatusText(homework.status) }}
                      </b-badge>
                    </td>
                    <td>
                      <span v-if="homework.grade">
                        <b-badge :variant="getGradeVariant(homework.grade)">
                          {{ homework.grade }}
                        </b-badge>
                      </span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <span v-if="homework.score !== null && homework.max_score">
                        {{ homework.score }}/{{ homework.max_score }}
                        <small class="text-muted">({{ getPercentage(homework.score, homework.max_score) }}%)</small>
                      </span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <b-button
                        v-if="homework.teacher_feedback || homework.submission_notes"
                        variant="outline-info"
                        size="sm"
                        @click="showHomeworkDetails(homework)"
                      >
                        Детали
                      </b-button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- No Results -->
            <div v-if="filteredHomework.length === 0" class="text-center py-5">
              <feather-icon icon="FileTextIcon" size="48" class="text-muted mb-3" />
              <h5 class="text-muted">Задания не найдены</h5>
              <p class="text-muted">Попробуйте изменить фильтры поиска</p>
            </div>

            <!-- Pagination -->
            <div v-if="filteredHomework.length > itemsPerPage" class="p-3 border-top">
              <b-pagination
                v-model="currentPage"
                :total-rows="filteredHomework.length"
                :per-page="itemsPerPage"
                align="center"
                size="sm"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Homework Details Modal -->
    <HomeworkDetailsModal
      v-model="showDetailsModal"
      :homework="selectedHomework"
    />
  </div>
</template>

<script>
import {
  BBadge,
  BFormSelect,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BButton,
  BPagination,
  BCard,
  BCardHeader,
  BCardBody
} from 'bootstrap-vue'
import HomeworkDetailsModal from './HomeworkDetailsModal.vue'

export default {
  name: 'HomeworkDetails',
  components: {
    BBadge,
    BFormSelect,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BButton,
    BPagination,
    BCard,
    BCardHeader,
    BCardBody,
    HomeworkDetailsModal
  },
  props: {
    homeworkData: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      statusFilter: 'all',
      gradeFilter: 'all',
      searchQuery: '',
      currentPage: 1,
      itemsPerPage: 10,
      showDetailsModal: false,
      selectedHomework: null,
      statusOptions: [
        { value: 'all', text: 'Все статусы' },
        { value: 'assigned', text: 'Назначено' },
        { value: 'in_progress', text: 'В работе' },
        { value: 'submitted', text: 'Сдано' },
        { value: 'graded', text: 'Оценено' },
        { value: 'late', text: 'Просрочено' },
        { value: 'missing', text: 'Не сдано' }
      ],
      gradeOptions: [
        { value: 'all', text: 'Все оценки' },
        { value: 'A+', text: 'A+' },
        { value: 'A', text: 'A' },
        { value: 'A-', text: 'A-' },
        { value: 'B+', text: 'B+' },
        { value: 'B', text: 'B' },
        { value: 'B-', text: 'B-' },
        { value: 'C+', text: 'C+' },
        { value: 'C', text: 'C' },
        { value: 'C-', text: 'C-' },
        { value: 'D', text: 'D' },
        { value: 'F', text: 'F' }
      ]
    }
  },
  computed: {
    filteredHomework() {
      let filtered = [...this.homeworkData]

      // Filter by status
      if (this.statusFilter !== 'all') {
        filtered = filtered.filter(hw => hw.status === this.statusFilter)
      }

      // Filter by grade
      if (this.gradeFilter !== 'all') {
        filtered = filtered.filter(hw => hw.grade === this.gradeFilter)
      }

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(hw =>
          hw.homework_title.toLowerCase().includes(query) ||
          (hw.teacher_feedback && hw.teacher_feedback.toLowerCase().includes(query))
        )
      }

      return filtered.sort((a, b) => new Date(b.assigned_at) - new Date(a.assigned_at))
    },
    paginatedHomework() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredHomework.slice(start, end)
    },
    gradeDistribution() {
      const distribution = {}
      this.homeworkData.forEach(hw => {
        if (hw.grade) {
          distribution[hw.grade] = (distribution[hw.grade] || 0) + 1
        }
      })
      return distribution
    }
  },
  methods: {
    getGradedCount() {
      return this.homeworkData.filter(hw => hw.status === 'graded').length
    },
    getPendingCount() {
      return this.homeworkData.filter(hw =>
        hw.status === 'submitted' || hw.status === 'in_progress'
      ).length
    },
    getAverageScore() {
      const gradedHomework = this.homeworkData.filter(hw => hw.score !== null && hw.max_score)
      if (gradedHomework.length === 0) return '0.0'

      const average = gradedHomework.reduce((sum, hw) => {
        return sum + (hw.score / hw.max_score) * 100
      }, 0) / gradedHomework.length

      return average.toFixed(1)
    },
    getStatusVariant(status) {
      const variants = {
        assigned: 'primary',
        in_progress: 'info',
        submitted: 'warning',
        graded: 'success',
        late: 'danger',
        missing: 'secondary'
      }
      return variants[status] || 'secondary'
    },
    getStatusText(status) {
      const texts = {
        assigned: 'Назначено',
        in_progress: 'В работе',
        submitted: 'Сдано',
        graded: 'Оценено',
        late: 'Просрочено',
        missing: 'Не сдано'
      }
      return texts[status] || status
    },
    getGradeVariant(grade) {
      const excellentGrades = ['A+', 'A', 'A-']
      const goodGrades = ['B+', 'B', 'B-']
      const satisfactoryGrades = ['C+', 'C', 'C-']

      if (excellentGrades.includes(grade)) return 'success'
      if (goodGrades.includes(grade)) return 'primary'
      if (satisfactoryGrades.includes(grade)) return 'info'
      if (grade === 'D') return 'warning'
      return 'danger'
    },
    getGradeColor(grade) {
      const colors = {
        'A+': '#28a745', 'A': '#28a745', 'A-': '#28a745',
        'B+': '#007bff', 'B': '#007bff', 'B-': '#007bff',
        'C+': '#17a2b8', 'C': '#17a2b8', 'C-': '#17a2b8',
        'D': '#ffc107', 'F': '#dc3545'
      }
      return colors[grade] || '#6c757d'
    },
    getHomeworkTypeText(type) {
      const types = {
        assignment: 'Задание',
        essay: 'Эссе',
        project: 'Проект',
        presentation: 'Презентация'
      }
      return types[type] || type
    },
    getDueDateClass(dueDate, status) {
      if (status === 'graded' || status === 'submitted') return ''

      const now = new Date()
      const due = new Date(dueDate)
      const timeDiff = due.getTime() - now.getTime()
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24))

      if (daysDiff < 0) return 'text-danger font-weight-bold'
      if (daysDiff <= 3) return 'text-warning font-weight-bold'
      return ''
    },
    getPercentage(score, maxScore) {
      return Math.round((score / maxScore) * 100)
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    },
    showHomeworkDetails(homework) {
      this.selectedHomework = homework
      this.showDetailsModal = true
    },
    createGradeChart() {
      const canvas = this.$refs.gradeChart
      if (!canvas || Object.keys(this.gradeDistribution).length === 0) return

      const ctx = canvas.getContext('2d')
      const distribution = this.gradeDistribution

      const centerX = canvas.width / 2
      const centerY = canvas.height / 2
      const radius = Math.min(centerX, centerY) - 10

      let startAngle = -Math.PI / 2
      const total = Object.values(distribution).reduce((sum, count) => sum + count, 0)

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      Object.entries(distribution).forEach(([grade, count]) => {
        const sliceAngle = (count / total) * 2 * Math.PI

        ctx.beginPath()
        ctx.arc(centerX, centerY, radius, startAngle, startAngle + sliceAngle)
        ctx.lineTo(centerX, centerY)
        ctx.fillStyle = this.getGradeColor(grade)
        ctx.fill()

        startAngle += sliceAngle
      })
    },
    createPerformanceChart() {
      const canvas = this.$refs.performanceChart
      if (!canvas) return

      const ctx = canvas.getContext('2d')
      const gradedHomework = this.homeworkData
        .filter(hw => hw.score !== null && hw.max_score)
        .sort((a, b) => new Date(a.assigned_at) - new Date(b.assigned_at))
        .slice(-10) // Last 10 assignments

      if (gradedHomework.length === 0) return

      const scores = gradedHomework.map(hw => (hw.score / hw.max_score) * 100)
      const maxScore = Math.max(...scores, 100)

      const padding = 40
      const chartWidth = canvas.width - 2 * padding
      const chartHeight = canvas.height - 2 * padding

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Draw grid
      ctx.strokeStyle = '#e0e0e0'
      ctx.lineWidth = 1

      for (let i = 0; i <= 10; i++) {
        const y = padding + (i * chartHeight) / 10
        ctx.beginPath()
        ctx.moveTo(padding, y)
        ctx.lineTo(padding + chartWidth, y)
        ctx.stroke()
      }

      // Draw line
      ctx.strokeStyle = '#007bff'
      ctx.lineWidth = 2
      ctx.beginPath()

      scores.forEach((score, index) => {
        const x = padding + (index * chartWidth) / (scores.length - 1)
        const y = padding + chartHeight - (score / maxScore) * chartHeight

        if (index === 0) {
          ctx.moveTo(x, y)
        } else {
          ctx.lineTo(x, y)
        }
      })

      ctx.stroke()

      // Draw points
      ctx.fillStyle = '#007bff'
      scores.forEach((score, index) => {
        const x = padding + (index * chartWidth) / (scores.length - 1)
        const y = padding + chartHeight - (score / maxScore) * chartHeight

        ctx.beginPath()
        ctx.arc(x, y, 4, 0, 2 * Math.PI)
        ctx.fill()
      })
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.createGradeChart()
      this.createPerformanceChart()
    })
  },
  watch: {
    homeworkData: {
      handler() {
        this.$nextTick(() => {
          this.createGradeChart()
          this.createPerformanceChart()
        })
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
.homework-details {
  .stats-card {
    border: none;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-2px);
    }
  }

  .legend-color {
    width: 12px;
    height: 12px;
    border-radius: 2px;
  }

  .table {
    .homework-row {
      &:hover {
        background-color: rgba(0, 0, 0, 0.05);
      }
    }

    th {
      font-weight: 600;
      color: #6c757d;
      border-top: none;
    }

    td {
      vertical-align: middle;
    }
  }

  .thead-light th {
    background-color: #f8f9fa;
  }
}

.card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.dark-layout {
  .homework-details {
    .stats-card,
    .card {
      background-color: #283046;
      color: #d0d2d6;
    }

    .thead-light th {
      background-color: #404656;
      color: #b4b7bd;
      border-color: #555;
    }

    .table {
      color: #d0d2d6;

      .homework-row:hover {
        background-color: rgba(255, 255, 255, 0.05);
      }

      td {
        border-color: #555;
      }
    }

    .text-muted {
      color: #b4b7bd !important;
    }
  }
}

@media (max-width: 768px) {
  .homework-details {
    .table-responsive {
      font-size: 0.9rem;
    }

    .stats-card {
      margin-bottom: 1rem;
    }
  }
}
</style>